<template>
  <div>
    <form @submit.prevent class="register">
      <h1 class="register__title">{{ $t("auth.welcome.title") }}</h1>

      <transition name="fade">
        <div class="register__row register__error-text" v-if="!!error">
          {{ error }}
        </div>
      </transition>

      <div class="register__row">
        <div class="register__row-col">
          <InputLabel>
            <template #label> {{ $t("common.lastName") }} * </template>

            <input
              type="text"
              v-validate="'required|alpha_spaces'"
              name="lastName"
              v-model="lastName"
              data-vv-as="Фамилия"
            />

            <template slot="error" v-if="errors.has('lastName')">
              {{ errors.first("lastName") }}
            </template>
          </InputLabel>
        </div>

        <div class="register__row-col">
          <InputLabel>
            <template #label> {{ $t("common.firstName") }} * </template>

            <input
              type="text"
              v-validate="'required|alpha_spaces'"
              name="firstName"
              v-model="firstName"
              data-vv-as="Имя"
            />

            <template slot="error" v-if="errors.has('firstName')">
              {{ errors.first("firstName") }}
            </template>
          </InputLabel>
        </div>
      </div>

      <div class="register__row">
        <div class="register__row-col">
          <InputLabel>
            <template #label>
              {{ $t("common.middleName") }}
            </template>

            <input
              type="text"
              v-model="middleName"
              name="middleName"
              v-validate="'alpha_spaces'"
              data-vv-as="Отчество"
            />

            <template slot="error" v-if="errors.has('middleName')">
              {{ errors.first("middleName") }}
            </template>
          </InputLabel>
        </div>

        <div class="register__row-col">
          <InputLabel>
            <template #label>
              {{ $t("common.phone") }}
            </template>

            <input
              type="tel"
              v-mask="'+##################'"
              name="phoneNumber"
              v-model="phoneNumber"
              placeholder="+7 900 000 00 00"
            />

            <template slot="error" v-if="errors.has('phoneNumber')">
              {{ errors.first("phoneNumber") }}
            </template>
          </InputLabel>
        </div>
      </div>

      <div class="register__row">
        <div class="register__row-col">
          <InputLabel>
            <template #label> {{ $t("common.password") }} * </template>

            <input
              type="password"
              name="password"
              v-validate="'required|min:8'"
              v-model="password"
              ref="password"
            />

            <template slot="error" v-if="errors.has('password')">
              {{ errors.first("password") }}
            </template>
          </InputLabel>
        </div>

        <div class="register__row-col">
          <InputLabel>
            <template #label> {{ $t("common.repeatPassword") }} * </template>

            <input
              type="password"
              name="passwordConfirmation"
              v-model="passwordConfirmation"
              v-validate="'required|confirmed:password'"
            />

            <template slot="error" v-if="errors.has('passwordConfirmation')">
              {{ errors.first("passwordConfirmation") }}
            </template>
          </InputLabel>
        </div>
      </div>

      <div class="register__row register__row_big_margin">
        <div class="register__row-col register__row-col_fw">
          <Checkbox v-model="notifications" class="register__checkbox">
            {{ $t("auth.register.subscribe") }}
          </Checkbox>

          <Checkbox
            v-model="terms"
            v-validate="'required'"
            data-vv-name="terms"
            :error="errors.has('terms')"
          >
            {{ $t("auth.register.consent1") }}

            <template v-if="locale === 'en'">
              <a
                href="/static/docs/Personal_data_processing_policy-2018_eng.pdf"
                class="register__checkbox-link"
                target="_blank"
              >
                {{ $t("auth.register.consent2") }}
              </a>
            </template>

            <template v-else>
              <a
                href="/static/docs/Personal_data_processing_policy_rus.pdf"
                class="register__checkbox-link"
                target="_blank"
              >
                {{ $t("auth.register.consent2") }}
              </a>
            </template>
          </Checkbox>
        </div>
      </div>

      <PrimaryButton
        type="button"
        @click.prevent="register"
        class="register__submit"
        :disabled="isSubmitDisabled"
      >
        {{ $t("auth.register.submit") }}
      </PrimaryButton>
    </form>
  </div>
</template>

<script>
import { PrimaryButton, Checkbox, InputLabel } from "@v2/shared/ui/components";

import apiUser from "@/api/user";

import { i18n } from "@v2/shared/config";
import getError from "@/helpers/error";

export default {
  name: "Welcome",

  components: { PrimaryButton, InputLabel, Checkbox },

  data() {
    return {
      lastName: "",
      firstName: "",
      middleName: "",
      email: "",
      phoneNumber: "",
      password: "",
      passwordConfirmation: "",
      terms: false,
      notifications: false,

      error: "",
      isSubmitDisabled: true,

      signature: "",
      expires: ""
    };
  },

  methods: {
    register() {
      this.$validator.validateAll().then(isValid => {
        if (!isValid) {
          return;
        }

        const id = this.$route.params.id;
        this.isSubmitDisabled = true;

        apiUser
          .completeRegister(id, this.$data)
          .then(({ data, token }) => {
            this.isSubmitDisabled = false;
            this.$store.commit("auth/LOGIN", { data, token });
            this.$router.push({ name: "home" });
          })
          .catch(response => {
            this.isSubmitDisabled = false;
            this.error = getError(response);
          });
      });
    }
  },

  computed: {
    locale() {
      return i18n.locale;
    }
  },

  watch: {
    terms(value) {
      this.isSubmitDisabled = !value;
    }
  },

  created() {
    if (this.$route.query.signature) {
      this.signature = this.$route.query.signature;
    }
    if (this.$route.query.expires) {
      this.expires = this.$route.query.expires;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@v2/app/styles/mixins";
@import "@v2/app/styles/media";

.register {
  padding: 48px 80px 64px 80px;
  width: 780px;
  box-sizing: border-box;
  position: relative;

  @include respond-down(lg) {
    width: 100%;
    max-width: 720px;

    padding-left: 50px;
    padding-right: 50px;
  }

  @include respond-down(md) {
    width: calc(100vw - 16 * 2px);
    min-width: 288px;

    padding: 24px;
  }

  &__title {
    @include h1();
    margin: 0 0 33px 0;
    @include respond-down(md) {
      font-size: 24px;
      margin-bottom: 12px;
    }
  }

  &__login-link {
    position: absolute;
    top: 64px;
    right: 80px;

    @include respond-down(md) {
      position: static;
      display: inline-block;

      margin-bottom: 22px;
    }
  }

  &__row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 33px;

    @include respond-up(md) {
      &_big_margin {
        margin-top: 15px;
      }

      &-col {
        width: 300px;

        &_fw {
          width: auto;
        }
      }
    }

    @include respond-down(md) {
      display: block;
      margin-bottom: 0;

      &-col {
        margin-bottom: 16px;
      }
    }
  }

  &__checkbox {
    display: flex;
    margin-bottom: 20px;

    &-link {
      @include hyperlink();
    }
  }

  &__submit {
    margin-top: 9px;
  }

  &__error-text {
    font-size: 12px;
    color: #ea5330;
  }
}
</style>
